import { useEffect, useRef, useState } from "react";
import {
  WSMCloudSyncState,
  WSMCloudSyncType,
} from "@WahooFitness/wsm-native/dist/esm/types/cloud_sync_event";
import { KCloudSyncState } from "@WahooFitness/wsm-native/dist/esm/types/kc/KCloudSyncState";
import { KCloudSyncType } from "@WahooFitness/wsm-native/dist/esm/types/kc/KCloudSyncType";
import { SensorManagerPluginPlugin } from "@WahooFitness/wsm-native";
import useCloudSyncListener from "@/hooks/useCloudSyncListener";
import { useWsmPlugin } from "@/hooks/useWsmPlugin";

// Twin of lobby-mfe/src/hooks/useVitalSummarySyncing.ts - keep the two in sync.

// KCommon's KCloudSyncType.VITAL_SUMMARY, which neither generated enum in the installed wsm-native
// carries yet: the string one (cloud_sync_event) stops at WorkoutProfile and the numeric one
// (types/kc) at TRAINING_PROGRESSION. Only the typings are behind — the native side forwards every
// sync type unfiltered — so both forms do arrive. Events carry the key, getSyncState the code.
const VITAL_SUMMARY_EVENT_TYPE = "VITAL_SUMMARY";
const VITAL_SUMMARY_SYNC_CODE = 29 as KCloudSyncType;

// A backfill writes a chunk of nights at a time and never waits for the uploads. Each write re-arms
// the manager's debounce and reports SCHEDULED at once, so the state only drops to READY when a
// push finishes before the next chunk has been read from HealthKit. That gap is a chunk read, a few
// seconds; holding the toast that long stops it blinking between chunks without lingering after
// the last push.
export const SYNC_SETTLE_MS = 3_000;

// addListener is asynchronous, so a READY that arrives between the seed effect's snapshot and the
// listener going live would be missed, leaving the toast open until the next VITAL_SUMMARY event.
// Re-poll on this interval to catch that gap, until either a live event arrives or the plugin
// reports settled.
export const SEED_RECHECK_MS = 10_000;

// Whether vital summaries are being uploaded right now, or undefined when there's no answer: the
// plugin is missing, there's no manager entry for vital summaries, or the call threw.
//
// KCommon's uploadQueued is what tells a sync that uploads nights from one that finds nothing to do
// (launch, sign-in, retry); the sync state alone is the same for both. The installed wsm-native
// typings predate it, hence the cast. An app too old to report it gets the old behaviour - any
// vital summary sync counts - rather than losing the toast before the app update lands.
const checkVitalSummarySyncing = async (
  plugin: SensorManagerPluginPlugin | undefined
): Promise<boolean | undefined> => {
  try {
    const syncState = await plugin?.getSyncState({ req: {} });
    const vitals = syncState?.rsp?.managers?.find(
      (manager) => manager.type === VITAL_SUMMARY_SYNC_CODE
    ) as { state?: KCloudSyncState; uploadQueued?: boolean } | undefined;
    if (!vitals) {
      return undefined;
    }
    const isSyncing =
      vitals.state === KCloudSyncState.SYNCING || vitals.state === KCloudSyncState.SCHEDULED;
    return typeof vitals.uploadQueued === "boolean" ? isSyncing && vitals.uploadQueued : isSyncing;
  } catch {
    return undefined;
  }
};

/**
 * True while vital summaries are going up - the 90-day Apple Health backfill, including the pauses
 * between its chunks, and the daily push. A sync with nothing to upload leaves it false.
 */
const useVitalSummarySyncing = () => {
  const { sensorManagerPlugin } = useWsmPlugin();
  const { addListener, removeListener } = useCloudSyncListener();
  const [syncing, setSyncing] = useState(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const sawEvent = useRef(false);
  // Bumped by every VITAL_SUMMARY event, so an answer to an older event's check can't overwrite a
  // newer event.
  const eventCount = useRef(0);

  // Events only announce transitions, so a screen opened part-way through a fill would sit dark
  // until the next one. Ask once for the state that is already true.
  useEffect(() => {
    let cancelled = false;
    const seed = async () => {
      const isSyncing = await checkVitalSummarySyncing(sensorManagerPlugin);
      // A live event is always fresher than this answer, so it wins.
      if (cancelled || sawEvent.current || isSyncing === undefined) {
        return;
      }
      if (isSyncing) {
        setSyncing(true);
      }
    };
    seed();
    return () => {
      cancelled = true;
    };
  }, [sensorManagerPlugin]);

  useEffect(() => {
    const callback = (type: WSMCloudSyncType, state: WSMCloudSyncState) => {
      if (String(type) !== VITAL_SUMMARY_EVENT_TYPE) {
        return;
      }
      sawEvent.current = true;
      clearTimeout(settleTimer.current);
      const thisEvent = ++eventCount.current;
      if (state === WSMCloudSyncState.Ready) {
        settleTimer.current = setTimeout(() => setSyncing(false), SYNC_SETTLE_MS);
      } else if (state === WSMCloudSyncState.Syncing || state === WSMCloudSyncState.Scheduled) {
        // The event can't say whether anything is being uploaded, so ask. No answer at all falls
        // back to the event itself, as before.
        void checkVitalSummarySyncing(sensorManagerPlugin).then((isUploading) => {
          if (thisEvent === eventCount.current) {
            setSyncing(isUploading ?? true);
          }
        });
      }
    };
    addListener(callback);
    return () => {
      removeListener(callback);
      clearTimeout(settleTimer.current);
    };
  }, [addListener, removeListener, sensorManagerPlugin]);

  // Covers the gap described above SEED_RECHECK_MS: while a seeded syncing state hasn't yet been
  // confirmed by a live event, keep asking the plugin. Once a real event lands, the listener owns
  // the state and this stops.
  useEffect(() => {
    if (!syncing || sawEvent.current) {
      return;
    }
    let cancelled = false;
    const interval = setInterval(async () => {
      if (sawEvent.current) {
        clearInterval(interval);
        return;
      }
      const isSyncing = await checkVitalSummarySyncing(sensorManagerPlugin);
      if (cancelled || sawEvent.current) {
        return;
      }
      if (isSyncing === false) {
        setSyncing(false);
      }
    }, SEED_RECHECK_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [syncing, sensorManagerPlugin]);

  return syncing;
};

export default useVitalSummarySyncing;
